import { z } from 'zod'
import { paginacaoSchema, type Pagina } from './comum'

/**
 * Leads que chegaram por anúncio de clique para o WhatsApp.
 *
 * O WhatsApp marca a primeira mensagem de quem clicou num anúncio com os dados
 * do anúncio. O sistema guarda isso e pede ao vendedor um julgamento simples —
 * o lead serve ou não serve —, e é esse julgamento, somado por anúncio, que diz
 * se o dinheiro do anúncio está trazendo cliente ou curioso.
 */

export const QUALIFICACAO_ANUNCIO = ['pendente', 'qualificado', 'desqualificado'] as const
export type QualificacaoAnuncio = (typeof QUALIFICACAO_ANUNCIO)[number]

export const ROTULO_QUALIFICACAO_ANUNCIO: Record<QualificacaoAnuncio, string> = {
  pendente: 'Sem avaliação',
  qualificado: 'Qualificado',
  desqualificado: 'Desqualificado',
}

/**
 * Voltar para `pendente` é permitido.
 *
 * O botão fica no alto da conversa, onde se clica sem querer. Sem caminho de
 * volta, um clique errado virava dado errado para sempre na conta do anúncio.
 */
export const qualificarLeadSchema = z.object({
  qualificacao: z.enum(QUALIFICACAO_ANUNCIO),
})

export const filtroAnunciosSchema = paginacaoSchema.extend({
  qualificacao: z.enum([...QUALIFICACAO_ANUNCIO, 'todas']).default('todas'),
  /** Recorta por um anúncio. É a chave de agrupamento da tela. */
  anuncio: z.string().trim().max(300).optional(),
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),
})

/** O que a conversa precisa saber para mostrar a faixa de qualificação. */
export interface LeadAnuncioResumoDto {
  id: string
  titulo: string | null
  urlOrigem: string | null
  fonte: string | null
  detectadoEm: string
  qualificacao: QualificacaoAnuncio
  qualificadoPorNome: string | null
  qualificadoEm: string | null
}

export interface LeadAnuncioDto extends LeadAnuncioResumoDto {
  chamadoId: string
  contatoId: string
  contatoNome: string
  /** Já formatado ou já mascarado pelo servidor. Nunca o número cru. */
  contatoTelefone: string | null
  telefoneMascarado: boolean
  anuncioId: string | null
  /** Quem atende a conversa, que nem sempre é quem qualificou. */
  responsavelNome: string | null
}

/**
 * Um anúncio e o que ele trouxe.
 *
 * `chave` é o id do anúncio quando o WhatsApp o informa, e o título quando
 * não: dois leads do mesmo anúncio precisam cair na mesma linha mesmo que o
 * id falte num deles.
 */
export interface ResumoPorAnuncioDto {
  chave: string
  titulo: string
  urlOrigem: string | null
  total: number
  qualificados: number
  desqualificados: number
  pendentes: number
}

export interface RespostaAnuncios extends Pagina<LeadAnuncioDto> {
  porAnuncio: ResumoPorAnuncioDto[]
  totais: { total: number; qualificados: number; desqualificados: number; pendentes: number }
}

/** Título usado quando o anúncio não informa nenhum. */
export const ANUNCIO_SEM_TITULO = 'Anúncio sem título'
